<template>
  <div class="browser-translate-toggle" :class="{ 'is-open': isOpen }">
    <button
      class="lang-toggle"
      type="button"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-controls="lang-menu"
      title="Change language"
      @click="toggleMenu"
    >
      <i class="fas fa-globe lang-toggle-icon" aria-hidden="true"></i>
      <span class="lang-toggle-current">{{ currentLangShort }}</span>
      <span class="lang-toggle-caret" aria-hidden="true"></span>
    </button>

    <div
      id="lang-menu"
      class="translate-switch"
      role="menu"
      aria-label="Language options"
      v-show="isOpen"
    >
      <button
        v-for="lang in languages"
        :key="lang.code"
        class="lang-btn"
        :class="{ active: currentLang === lang.code, loading: loading }"
        :disabled="loading || currentLang === lang.code"
        @click="changeLanguage(lang.code)"
      >
        {{ lang.name }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BrowserTranslateToggle',
  data() {
    return {
      isOpen: false,
      loading: false,
      currentLang: 'en',
      languages: [
        { code: 'en', label: 'EN', name: 'English' },
        { code: 'fr', label: 'FR', name: 'Français' },
        { code: 'es', label: 'ES', name: 'Español' }
      ]
    }
  },
  computed: {
    currentLangShort() {
      const match = this.languages.find(lang => lang.code === this.currentLang)
      return match ? match.label : 'EN'
    }
  },
  mounted() {
    this.syncState()
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleMenu() {
      if (this.loading) return
      this.isOpen = !this.isOpen
    },
    handleClickOutside(event) {
      if (!this.$el || this.$el.contains(event.target)) return
      this.isOpen = false
    },
    notify(message, color) {
      if (!this.$store || !this.$store.commit) {
        return
      }

      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color || 'teal darken-4')
      this.$store.commit('activateSnackBar', true)
    },
    syncState() {
      if (!this.$translator) {
        this.currentLang = 'en'
        return
      }

      this.currentLang = this.$translator.getLanguage()
    },
    async changeLanguage(lang) {
      if (this.loading || !this.$translator || this.currentLang === lang) {
        return
      }

      this.loading = true

      try {
        const languageName = { en: 'English', fr: 'Français', es: 'Español' }
        this.notify('Applying language: ' + languageName[lang] + '...', 'blue darken-2')

        let result
        const applyPromise = this.$translator.setLanguage(lang)
        const timeoutPromise = new Promise(resolve => {
          setTimeout(() => resolve({ applied: false, excluded: false, timedOut: true }), 25000)
        })
        result = await Promise.race([applyPromise, timeoutPromise])
        this.syncState()

        const doneMessage = {
          en: 'Language set to English.',
          fr: 'Langue changée en français.',
          es: 'Idioma cambiado a español.'
        }

        if (result && result.timedOut) {
          this.notify('Translation is slow right now, it will finish in the background.', 'orange darken-2')
        } else if (result && result.excluded) {
          this.notify('Language saved, but translation is disabled on this page to keep layout stable.', 'orange darken-2')
        } else {
          this.notify(doneMessage[this.currentLang] || 'Language updated.', 'teal darken-4')
        }
      } catch (error) {
        this.notify('Unable to change language.', 'red darken-2')
      } finally {
        this.loading = false
        this.isOpen = false
      }
    }
  }
}
</script>

<style scoped>
.browser-translate-toggle {
  position: fixed;
  left: 50%;
  top: 16px;
  transform: translateX(-50%);
  z-index: 1001;
}

.browser-translate-toggle.is-open {
  z-index: 4000;
}

.translate-switch {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 170px;
  background: #ffffff;
  border: 1px solid rgba(21, 101, 192, 0.35);
  border-radius: 14px;
  padding: 4px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.22);
}

.lang-toggle {
  border: 0;
  width: 100%;
  padding: 9px 12px;
  border-radius: 10px;
  background: transparent;
  color: #0f172a;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}

.lang-toggle:hover {
  background: rgba(21, 101, 192, 0.08);
}

.lang-toggle-icon {
  font-size: 12px;
}

.lang-toggle-text {
  opacity: 0.95;
}

.lang-toggle-current {
  margin-left: auto;
  color: #1565c0;
  font-weight: 800;
}

.lang-toggle-caret {
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid #334155;
  transition: transform 0.2s ease;
}

.browser-translate-toggle.is-open .lang-toggle-caret {
  transform: rotate(180deg);
}

.lang-btn {
  border: 0;
  width: 100%;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  color: #334155;
  background: transparent;
  transition: all 0.2s ease;
}

.lang-btn:hover:not(:disabled) {
  color: #0f172a;
  background: rgba(21, 101, 192, 0.08);
}

.lang-btn.active {
  color: #ffffff;
  background: linear-gradient(120deg, #1565c0, #1e88e5);
  box-shadow: 0 6px 14px rgba(30, 136, 229, 0.35);
}

.lang-btn.loading,
.lang-btn:disabled {
  opacity: 0.7;
  cursor: default;
}

@media (max-width: 768px) {
  .browser-translate-toggle {
    left: 50%;
    top: 10px;
    transform: translateX(-50%);
  }

  .translate-switch {
    min-width: 152px;
  }
}
</style>
